import { Location } from "@/types/setup"
import { Country } from "@/types/country"
import { Region } from "@/types/region"
import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import { RootState } from "@/store/index"
import axios from "axios"
import { Timezone } from "@/types/timezone"
import { User } from "@/types/user"

const STORE_NAME = "locationSettings"

export enum LoadingTypes {
    COUNTRY_LIST = "COUNTRY_LIST",
    REGION_LIST = "REGION_LIST",
    TIMEZONE_LIST = "TIMEZONE_LIST",
    LOCATION = "LOCATION",
    AVAILABLE_USERS = "AVAILABLE_USERS",
}

export type GeoLocationCoordinates = {
    latitude: number | null
    longitude: number | null
}

export type PutLocationForm = {
    data: {
        name: string
        spotRegion?: {
            type?: string
            code?: string
        }
        coordinates?: GeoLocationCoordinates
        countryCode?: string
        timezone: string | null
    }
}

export type LocationSettingsState = {
    location: Location | null
    countryList: Country[]
    regionList: Region[] | null
    timezone: Timezone | null
    timezoneList: Timezone[]
    availableUsers: User[]
    loading: Record<LoadingTypes, boolean>
}

type CommonApiResponse<T> = {
    data: T
}

type LocationApiResponse = CommonApiResponse<{
    location: Location
}>

type CountriesApiResponse = CommonApiResponse<{
    countries: Country[]
}>

type RegionsApiResponse = CommonApiResponse<{
    entries: Region[]
}>

type TimezonesApiResponse = CommonApiResponse<{
    timezones: Timezone[]
}>

type AvailableUsersApiResponse = CommonApiResponse<{
    users: User[]
}>

export enum LocationSettingsMutations {
    SET_LOADING = "setLoading",
    SET_COUNTRY_LIST = "setCountryList",
    SET_REGION_LIST = "setRegionList",
    SET_TIMEZONE_LIST = "setTimezoneList",
    SET_LOCATION = "setLocation",
    SET_AVAILABLE_USERS = "setAvailableUsers",
}

export const LocationSettingsActions = {
    FETCH_LOCATION: `${STORE_NAME}/fetchLocation`,
    FETCH_REGION_LIST: `${STORE_NAME}/fetchRegionList`,
    FETCH_COUNTRY_LIST: `${STORE_NAME}/fetchCountryList`,
    FETCH_TIMEZONE_LIST: `${STORE_NAME}/fetchTimezoneList`,
    POST_FORM_DATA: `${STORE_NAME}/postFormData`,
    FETCH_AVAILABLE_USERS: `${STORE_NAME}/fetchAvailableUsers`,
    CHANGE_OWNER: `${STORE_NAME}/changeOwner`,
} as const

const state: LocationSettingsState = {
    location: null,
    countryList: [],
    regionList: [],
    timezone: null,
    timezoneList: [],
    availableUsers: [],
    loading: {
        [LoadingTypes.COUNTRY_LIST]: false,
        [LoadingTypes.REGION_LIST]: false,
        [LoadingTypes.TIMEZONE_LIST]: false,
        [LoadingTypes.LOCATION]: false,
        [LoadingTypes.AVAILABLE_USERS]: false,
    },
}

const mutations: MutationTree<LocationSettingsState> = {
    setLocation(state, location: Location) {
        state.location = location
    },
    setLoading(state, payload: { type: LoadingTypes; loading: boolean }) {
        state.loading = {
            ...state.loading,
            [LoadingTypes[payload.type]]: payload.loading,
        }
    },
    setCountryList(state, countryList: Country[]) {
        state.countryList = countryList
    },
    setRegionList(state, regionList: Region[]) {
        state.regionList = regionList
    },
    setTimezoneList(state, timezoneList: Timezone[]) {
        state.timezoneList = timezoneList
    },
    setAvailableUsers(state, users: User[]) {
        state.availableUsers = users
    },
}

const actions: ActionTree<LocationSettingsState, RootState> = {
    async fetchLocation({ commit }, locationId) {
        commit(LocationSettingsMutations.SET_LOADING, {
            type: LoadingTypes.LOCATION,
            loading: true,
        })

        try {
            const res = await axios.get<LocationApiResponse>(`/locations/${locationId}`)
            commit(LocationSettingsMutations.SET_LOCATION, res.data.data.location)
        } catch (e) {
            console.log(e)
        } finally {
            commit(LocationSettingsMutations.SET_LOADING, {
                type: LoadingTypes.LOCATION,
                loading: false,
            })
        }
    },
    async fetchCountryList({ commit }) {
        commit(LocationSettingsMutations.SET_LOADING, {
            type: LoadingTypes.COUNTRY_LIST,
            loading: true,
        })

        try {
            const res = await axios.get<CountriesApiResponse>("/countries?visible=true")
            commit(LocationSettingsMutations.SET_COUNTRY_LIST, res.data.data.countries)
        } catch (e) {
            console.log(e)
        } finally {
            commit(LocationSettingsMutations.SET_LOADING, {
                type: LoadingTypes.COUNTRY_LIST,
                loading: false,
            })
        }
    },
    async fetchRegionList({ commit }) {
        commit(LocationSettingsMutations.SET_LOADING, {
            type: LoadingTypes.REGION_LIST,
            loading: true,
        })

        try {
            const res = await axios.get<RegionsApiResponse>("/pricing/spot-market/regions")
            commit(LocationSettingsMutations.SET_REGION_LIST, res.data.data.entries)
        } catch (e) {
            console.log(e)
        } finally {
            commit(LocationSettingsMutations.SET_LOADING, {
                type: LoadingTypes.REGION_LIST,
                loading: false,
            })
        }
    },
    async fetchTimezoneList({ commit }, countryCode: string) {
        commit(LocationSettingsMutations.SET_LOADING, {
            type: LoadingTypes.TIMEZONE_LIST,
            loading: true,
        })

        try {
            const res = await axios.get<TimezonesApiResponse>(
                `/timezones?countryCode=${countryCode}`,
            )
            commit(LocationSettingsMutations.SET_TIMEZONE_LIST, res.data.data.timezones)
        } catch (e) {
            console.log(e)
        } finally {
            commit(LocationSettingsMutations.SET_LOADING, {
                type: LoadingTypes.TIMEZONE_LIST,
                loading: false,
            })
        }
    },
    async fetchAvailableUsers({ commit }, companyId: string) {
        commit(LocationSettingsMutations.SET_LOADING, {
            type: LoadingTypes.AVAILABLE_USERS,
            loading: true,
        })

        try {
            const res = await axios.get<AvailableUsersApiResponse>(`/companies/${companyId}/users`)
            commit(LocationSettingsMutations.SET_AVAILABLE_USERS, res.data.data.users ?? [])
        } catch (e) {
            console.log(e)
            commit(LocationSettingsMutations.SET_AVAILABLE_USERS, [])
        } finally {
            commit(LocationSettingsMutations.SET_LOADING, {
                type: LoadingTypes.AVAILABLE_USERS,
                loading: false,
            })
        }
    },
    async changeOwner(
        { commit, dispatch },
        payload: { locationId: string | number; userId: number },
    ) {
        commit(LocationSettingsMutations.SET_LOADING, {
            type: LoadingTypes.LOCATION,
            loading: true,
        })

        try {
            await axios.put(`/locations/${payload.locationId}/owner`, {
                data: { userId: payload.userId },
            })

            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "location.owner_change_success",
                    color: "green",
                    isTranslation: true,
                },
                { root: true },
            )
        } catch (e) {
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "location.owner_change_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit(LocationSettingsMutations.SET_LOADING, {
                type: LoadingTypes.LOCATION,
                loading: false,
            })
        }
    },
    async postFormData({ commit, state, dispatch }, data: PutLocationForm) {
        commit(LocationSettingsMutations.SET_LOADING, {
            type: LoadingTypes.LOCATION,
            loading: true,
        })

        try {
            await axios.put(`/locations/${state?.location?.id}`, data)

            dispatch(
                "toastMessageModule/showMessage",
                { message: "location.save_success", color: "green", isTranslation: true },
                { root: true },
            )
        } catch (e) {
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "location.save_error.",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit(LocationSettingsMutations.SET_LOADING, {
                type: LoadingTypes.LOCATION,
                loading: false,
            })
        }
    },
}

const getters: GetterTree<LocationSettingsState, RootState> = {
    countryList: (state) => state.countryList,
    location: (state) => state.location,
    regionList: (state) => (countryId: string) => {
        return (
            state.regionList?.filter((region) => region?.country?.id === parseInt(countryId)) || []
        )
    },
    timezone: ({ regionList }) => regionList,
    timezoneList: ({ timezoneList }) => timezoneList,
    availableUsers: ({ availableUsers }) => availableUsers,
    loading: ({ loading }) => loading,
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
} as Module<LocationSettingsState, RootState>
